import React from 'react';
import { Box, Typography, Paper, Avatar, Divider, Chip } from '@mui/material';
import { format } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import { styled } from '@mui/material/styles';
import { getTaskStatusColor } from '../../utils/helper';
import TimelineItemCard from './TimelineItemCard';

// Styled components
const TimelineContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  height: '100%',
  boxShadow: 'none',
  border: '1px solid #E5E7EB',
  display: 'flex',
  flexDirection: 'column',
}));

const TimelineHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexShrink: 0,
  borderBottom: '1px solid #E5E7EB',
}));

const TimelineContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 0.5, 0.5, 0.5),
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#F9FAFB',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#D1D5DB',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#9CA3AF',
  },
}));


const Timeline = ({ 
  title = 'Upcoming Tasks and Events', 
  subtitle = 'See your upcoming tasks',
  events = [], 
  onEventClick,
  showEditButton = false,
  onEditClick
}) => {
  if (!events || events.length === 0) {
    return (
      <TimelineContainer>
        <TimelineHeader>
          <Box>
            <Typography 
              sx={{ 
                color: '#1E293B', 
                fontWeight: 700, 
                fontSize: '0.875rem',
                mb: 0.25
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                sx={{ 
                  color: '#64748B', 
                  fontSize: '0.75rem',
                  fontWeight: 500
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {showEditButton && (
            <Box 
              onClick={onEditClick}
              sx={{ 
                width: 28, 
                height: 28, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                borderRadius: '50%',
                bgcolor: '#F3F4F6',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#E5E7EB'
                }
              }}
            >
              <EditIcon sx={{ fontSize: '0.75rem', color: '#6B7280' }} />
            </Box>
          )}
        </TimelineHeader>
        <TimelineContent>
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Typography sx={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500 }}>
              No upcoming events
            </Typography>
          </Box>
        </TimelineContent>
      </TimelineContainer>
    );
  }

  return (
    <TimelineContainer>
      <TimelineHeader>
          <Box>
            <Typography 
              sx={{ 
                color: '#1E293B', 
                fontWeight: 700, 
                fontSize: '1rem',
                mb: 0.25
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography 
                sx={{ 
                  color: '#64748B', 
                  fontSize: '0.875rem',
                  fontWeight: 500
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        {showEditButton && (
          <Box 
            onClick={onEditClick}
            sx={{ 
              width: 28, 
              height: 28, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              borderRadius: '50%',
              bgcolor: '#F3F4F6',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#E5E7EB'
              }
            }}
          >
            <EditIcon sx={{ fontSize: '0.75rem', color: '#6B7280' }} />
          </Box>
        )}
      </TimelineHeader>

      <TimelineContent>
        {events.map((event) => (
          <TimelineItemCard
            key={event.id}
            item={event}
            onClick={onEventClick}
          />
        ))}
      </TimelineContent>
    </TimelineContainer>
  );
};

export default Timeline; 